import { weekEnd, weekStart } from '../../lib/calc/week';
import type { PnlScopeQuery } from '../../lib/router';
import { todayIso } from '../../lib/store';

export { todayIso };

/** The P&L scope in a URL query, defaulting to today. A `?week=` is any date in the week meant; it is normalised to that week's Monday here so the picker, the label and the server all agree on which seven days. */
export function readScope(query: URLSearchParams): PnlScopeQuery {
  const month = query.get('month');
  if (month && /^\d{4}-\d{2}$/.test(month)) return { month };
  const week = query.get('week');
  if (week) {
    const monday = weekStart(week);
    if (monday !== null) return { week: monday };
  }
  const day = query.get('day');
  if (day && /^\d{4}-\d{2}-\d{2}$/.test(day)) return { day };
  return { day: todayIso() };
}

export function scopeLabel(scope: PnlScopeQuery): string {
  if (scope.month) return scope.month;
  if (scope.week) return `${scope.week} – ${weekEnd(scope.week) ?? ''}`;
  return scope.day ?? '';
}

/*
 * The Total Breakdown's period is a SECOND scope, not a widening of the one
 * above. The dashboard asks "which day, or which month"; Total Breakdown asks
 * "which month, or which calendar year". Folding both into `readScope` would
 * give it three keys and force every caller — and every reader — to work out
 * which two of the three its screen actually honours. Two small readers, each
 * answering one question, stay readable.
 */

/** A Total Breakdown period: exactly one of a month or a calendar year. */
export type PeriodScope = { month: string } | { year: string };

const MONTH_KEY = /^\d{4}-\d{2}$/;
const YEAR_KEY = /^\d{4}$/;

/**
 * The period in a URL query, defaulting to this month.
 *
 * A month beats a year when both are somehow present, and anything malformed
 * is ignored rather than passed on: the server rejects a bad scope with a
 * validation error, and a mistyped URL should land the owner on this month's
 * figures, not on an error banner.
 */
export function readPeriod(query: URLSearchParams): PeriodScope {
  const month = query.get('month');
  if (month && MONTH_KEY.test(month)) return { month };
  const year = query.get('year');
  if (year && YEAR_KEY.test(year)) return { year };
  return { month: todayIso().slice(0, 7) };
}

/*
 * Month names spelled out rather than read off `toLocaleDateString`: a
 * 'YYYY-MM' string handed to `new Date()` is parsed as UTC midnight and then
 * printed in the reader's own zone, which in Vancouver turns August into
 * July. A lookup cannot drift.
 */
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

/** The period as a person would say it: 'August 2026', or '2026'. */
export function periodLabel(period: PeriodScope): string {
  if ('year' in period) return period.year;
  const [year, month] = period.month.split('-');
  return `${MONTH_NAMES[Number(month) - 1] ?? period.month} ${year}`;
}

/*
 * The day last looked at, so Day -> Month -> Day lands back where the user was
 * rather than on the 1st. Module state on purpose: the picker is remounted when
 * moving between the dashboard and a project, and a reload should simply start
 * on today again — remembering across reloads would be a surprise, not a help.
 */
let lastDay: string | null = null;

export function rememberDay(day: string): void {
  lastDay = day;
}

/** The day to return to for `month`: the remembered one if it is in that month, else the 1st. */
export function dayFor(month: string): string {
  return lastDay && lastDay.startsWith(`${month}-`) ? lastDay : `${month}-01`;
}

/** The day to return to for a week (its Monday): the remembered one if it falls in that week, else the Monday itself. Same bargain as `dayFor`. */
export function dayForWeek(weekMonday: string): string {
  return lastDay && weekStart(lastDay) === weekMonday ? lastDay : weekMonday;
}

/*
 * A month as a chart tick. It lives here, beside `periodLabel`, because two
 * different charts on the Total Breakdown now label the same twelve months —
 * one from the bank, one from the Daily Reports — and they sit directly above
 * one another. Two copies of this function would eventually disagree about
 * whether August is 'Aug' or 'Aug 2026', and the two charts would stop
 * looking like the same twelve months.
 */
const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * 'Aug', or "Jan '26" — January carries the year and the other eleven do not.
 * A twelve-month window ending at a month crosses a New Year, so two bars a
 * year apart would otherwise both read 'Aug'.
 */
export function shortMonthLabel(month: string): string {
  const [year, mon] = month.split('-');
  const name = SHORT_MONTHS[Number(mon) - 1] ?? month;
  return mon === '01' ? `${name} ’${year.slice(2)}` : name;
}
